import React, { ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  HomeIcon,
  MapPinIcon,
  DocumentChartBarIcon,
  UserIcon,
  ClipboardDocumentCheckIcon,
  ArrowRightOnRectangleIcon,
  ClockIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline'
import ThemeToggle from './ThemeToggle'
import NotificationBell from './NotificationBell'


/**
 * Props interface for MobileLayout component
 * @interface MobileLayoutProps
 * @property {ReactNode} children - The child components to render within the layout
 * @property {any} user - The current user object
 * @property {() => Promise<void>} logout - The logout function
 */
interface MobileLayoutProps {
  children: ReactNode
  user: any
  logout: () => Promise<void>
}

/**
 * MobileLayout component provides the main layout structure for the mobile app
 * including header with user profile, navigation, and logout functionality
 * @param {MobileLayoutProps} props - Component props containing children, user, and logout function
 * @returns {JSX.Element} The mobile layout component
 */
export default function MobileLayout({ children, user, logout }: MobileLayoutProps) {
  const location = useLocation()
  const navigate = useNavigate()

  const navigation = [
  { name: 'Status', href: '/status', icon: ClockIcon, glow: 'glow-green' },
  { name: 'Reports', href: '/reports', icon: DocumentChartBarIcon, glow: 'glow-blue' },
  { name: 'Locations', href: '/locations', icon: MapPinIcon, glow: 'glow-purple' },
  { name: 'Profile', href: '/profile', icon: UserIcon, glow: 'glow-orange' },
  { name: 'Dashboard', href: '/dashboard', icon: ChartBarIcon, glow: 'glow-cyan' },
]

  /**
   * Handles user logout by calling the logout function and navigating to login page
   * @returns {Promise<void>}
   */
  const handleLogout = async () => {
    try {
      await logout()
      navigate('/login')
    } catch (error) {
      console.error('Logout error:', error)
    }
  }

  return (
    <div className="min-h-screen bg-white dark:bg-tech-900">
      {/* Header - Fixed height to prevent jumping */}
      <header className="header-bar safe-area-top sticky top-0 z-50 bg-white dark:bg-tech-900 border-b border-gray-200 dark:border-tech-700">
        <div className="px-4 py-4 flex items-center justify-between min-h-[80px]">
          <div className="flex items-center space-x-3">
            {user?.currentOrganization?.logoUrl && (
              <div className="relative">
                <img 
                  src={user.currentOrganization.logoUrl} 
                  alt={user.currentOrganization.name}
                  className="h-8 w-8 rounded object-cover transition-all duration-300"
                />
              </div>
            )}
            <div className="transition-all duration-300">
              <h1 className="text-heading-sm font-display bg-gradient-to-r from-gray-900 to-gray-700 dark:from-tech-50 dark:to-tech-200 bg-clip-text text-transparent">
                {user?.currentOrganization?.name || 'Security App'}
              </h1>
              <p className="text-caption text-primary-500 dark:text-primary-400 uppercase tracking-wider font-mono">
                {user?.currentOrganization?.name ? 'Mobile App' : 'Connect'}
              </p>
            </div>
          </div>
          
          {user && (
            <div className="flex items-center space-x-3">
              <NotificationBell />
              <ThemeToggle />
            </div>
          )}
        </div>
      </header>

      {/* Main Content - Fixed height container to prevent jumping */}
      <main className="flex-1 pb-20 min-h-[calc(100vh-140px)]">
        <div className="animate-fade-in transition-all duration-300">
          {children}
        </div>
      </main>

      {/* Bottom Navigation - Fixed height */}
      <nav className="mobile-nav fixed bottom-0 left-0 right-0 safe-area-bottom z-40 bg-white dark:bg-tech-900 border-t border-gray-200 dark:border-tech-700 min-h-[80px]">
        <div className="flex justify-around py-2 h-full items-center">
          {navigation.map((item, index) => {
            const isActive = location.pathname === item.href || 
                           (item.href === '/dashboard' && location.pathname === '/') ||
                           (item.name === 'Reports' && ['/reports', '/incident-report', '/hourly-report'].includes(location.pathname))
            
            return (
              <button
                key={item.name}
                className={`nav-item flex-col min-h-[60px] p-2 transition-all duration-300 ease-in-out animate-slide-up delay-${index * 100} ${
                  isActive ? 'text-primary-400 transform scale-105' : 'text-gray-500 dark:text-tech-400 hover:scale-105'
                }`}
                onClick={() => navigate(item.href)}
              >
                <div className={`p-2 rounded-lg transition-all duration-300 ease-in-out ${
                  isActive ? 'bg-primary-500/20 shadow-glow-sm' : 'bg-gray-100/50 dark:bg-tech-800/50 hover:bg-gray-200/50 dark:hover:bg-tech-700/50'
                }`}>
                  <item.icon className={`w-5 h-5 stat-icon transition-all duration-300 ${isActive ? item.glow : ''}`} />
                </div>
                <span className="text-caption mt-1 font-medium transition-all duration-300">{item.name}</span>
                {isActive && (
                  <div className="w-1 h-1 bg-primary-500 rounded-full mt-1 animate-pulse"></div>
                )}
              </button>
            )
          })}
        </div>
      </nav>
    </div>
  )
} 